import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Box, ArrowLeft, Shield } from 'lucide-react';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-bg-dark">
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: 'https://printmindai.com/' },
          { name: 'Privacy Policy', url: 'https://printmindai.com/privacy' },
        ]}
      />
      {/* Navbar */}
      <nav className="border-b border-text-muted/10 bg-bg-dark/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={() => navigate('/')} className="flex items-center space-x-3 hover:opacity-80 transition">
              <div className="relative">
                <Brain className="w-8 h-8 text-primary" />
                <Box className="w-4 h-4 text-accent absolute -bottom-0.5 -right-0.5" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                PrintMind AI
              </span>
            </button>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-text-muted hover:text-text-primary transition text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-primary/15 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-text-primary" data-testid="privacy-title">Privacy Policy</h1>
            <p className="text-text-muted text-sm mt-1">Last updated: April 5, 2026</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none space-y-8 text-text-secondary text-sm leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">1. Introduction</h2>
            <p>
              Welcome to PrintMind AI, operated by <strong className="text-text-primary">Harbor Momentum Group LLC</strong> ("we," "us," or "our").
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our
              platform at printmindai.com (the "Service"). Please read this policy carefully. By accessing or using PrintMind AI,
              you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">2. Information We Collect</h2>
            <h3 className="text-base font-medium text-text-primary mb-2">2.1 Account Information</h3>
            <p>When you register, we collect your email address and an encrypted password. We do not store plain-text passwords.</p>

            <h3 className="text-base font-medium text-text-primary mb-2 mt-4">2.2 Uploaded Files (STL / 3D Models)</h3>
            <p>
              When you upload STL or other 3D model files for analysis, repair, or support generation, those files are temporarily
              stored on our servers for processing purposes only. We do <strong className="text-text-primary">not</strong> claim any
              ownership or intellectual property rights over your uploaded files. Your 3D models remain your sole property at all times.
            </p>

            <h3 className="text-base font-medium text-text-primary mb-2 mt-4">2.3 Usage Data</h3>
            <p>
              We automatically collect certain information about how you interact with the Service, including pages visited,
              features used, generation counts, and performance metrics. This data is used to improve the Service and is not
              shared with third parties for advertising purposes.
            </p>

            <h3 className="text-base font-medium text-text-primary mb-2 mt-4">2.4 Payment Information</h3>
            <p>
              Payment processing is handled by Stripe, Inc. We do not store credit card numbers or full payment details on our servers.
              Please refer to <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Stripe's Privacy Policy</a> for details on their data handling practices.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">3. Protection of Uploaded 3D Files</h2>
            <p>
              We take the privacy and intellectual property of your uploaded STL and 3D files very seriously:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Your 3D files are processed solely for the purpose of providing the requested service (analysis, repair, support generation, or AI diagnosis).</li>
              <li>We do <strong className="text-text-primary">not</strong> use your uploaded files to train AI/ML models.</li>
              <li>We do <strong className="text-text-primary">not</strong> share, sell, license, or distribute your 3D files to any third party.</li>
              <li>Uploaded files are stored in encrypted storage and are accessible only to your account.</li>
              <li>You may delete your uploaded files and generated outputs at any time from your account dashboard.</li>
              <li>Upon account deletion, all associated files are permanently removed from our servers within 30 days.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">4. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide and maintain the Service, including STL analysis, repair, support generation, and AI-powered diagnostics.</li>
              <li>To process your transactions and manage your subscription.</li>
              <li>To send you service-related communications (account verification, security alerts, billing notices).</li>
              <li>To monitor usage patterns and improve the Service's performance and features.</li>
              <li>To detect, prevent, and address technical issues or abuse.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">5. Data Sharing and Disclosure</h2>
            <p>We do not sell your personal information. We may share information only in the following limited circumstances:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong className="text-text-primary">Service Providers:</strong> We use third-party services (Stripe for payments, cloud hosting providers) that process data on our behalf under strict contractual obligations.</li>
              <li><strong className="text-text-primary">Legal Compliance:</strong> We may disclose information if required by law, regulation, or valid legal process.</li>
              <li><strong className="text-text-primary">Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your data may be transferred as part of the transaction.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">6. Data Security</h2>
            <p>
              We implement industry-standard security measures including encryption in transit (TLS/SSL), encrypted storage,
              secure authentication with hashed passwords, and regular security reviews. However, no method of transmission
              over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">7. Data Retention</h2>
            <p>
              We retain your account information for as long as your account is active. Uploaded 3D files and generated outputs
              are retained until you delete them or until your account is terminated. Aggregated, anonymized usage statistics
              may be retained indefinitely for analytics purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">8. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Access, correct, or delete your personal information.</li>
              <li>Export your data in a portable format.</li>
              <li>Withdraw consent for data processing.</li>
              <li>Request restriction of processing.</li>
              <li>Lodge a complaint with a supervisory authority.</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, contact us at <a href="mailto:support@printmind.ai" className="text-primary hover:underline">support@printmind.ai</a>.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">9. Children's Privacy</h2>
            <p>
              PrintMind AI is not intended for use by individuals under the age of 13. We do not knowingly collect personal
              information from children under 13. If we become aware that we have collected such information, we will take
              steps to delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the
              updated policy on this page with a revised "Last updated" date. Your continued use of the Service after changes
              constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">11. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-3 p-4 bg-bg-light rounded-xl border border-text-muted/20">
              <p className="font-medium text-text-primary">Harbor Momentum Group LLC</p>
              <p>d/b/a PrintMind AI</p>
              <p className="mt-2">
                Email: <a href="mailto:support@printmind.ai" className="text-primary hover:underline">support@printmind.ai</a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
